import React, { forwardRef } from 'react';
import { Pressable } from 'react-native';
import Icon, { IconColorVariant, IconName, IconSize } from '../Icon';
import { Shadow, colors, shadows } from '../../../utils/theme';
import Text, { TextColor } from '../Text';
import Stack from '../Stack';
import {
    DimensionValue,
    GestureResponderEvent,
    LayoutChangeEvent,
    StyleProp,
    TextStyle,
    ViewStyle,
} from 'react-native';
import { StateStyles, useInteractionStateStyles } from '../../../hooks/useInteractionStateStyles';
import { useIsDisabledByParent } from '../IsDisabledProvider';
import { getSpacing } from '../../../utils/spacing';
import AppleLogo from './AppleLogo';
import GoogleLogo from './GoogleLogo';
import { getIntl } from '../../../utils/intl';
import { View } from 'react-native';
import { hexToRgbA } from '../../../utils/hexToRGBA';

const intl = getIntl('Button', {
    apple: 'Sign in with Apple',
    google: 'Sign in with Google',
});

export type ButtonVariant =
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'alert'
    | 'text'
    | 'text-dark'
    | 'select'
    | 'ghost'
    | 'list-button'
    | 'pro-header-button';

export type RenderButtonContentArgs = {
    left: React.ReactNode | null;
    right: React.ReactNode;
    center: React.ReactNode | null;
};

export type RenderButtonContent = (args: RenderButtonContentArgs) => React.ReactNode;

export type ButtonOwnProps = {
    variant?: ButtonVariant;
    icon?: IconName;
    size?: 'lg' | 'sm';
    isDisabled?: boolean;
    onPress: (e: GestureResponderEvent) => void;
    testID?: string;
    onLayout?: (event: LayoutChangeEvent) => void;
    leftIcon?: IconName;
    leftContent?: React.ReactNode;
    justifyButtonContent?: 'center' | 'flex-start';
    textStyle?: StyleProp<TextStyle>;
    iconColor?: IconColorVariant;
    width?: DimensionValue;
    renderButtonContent?: RenderButtonContent;
    style?: StyleProp<ViewStyle>;
} & (
    | {
          title: string;
      }
    | {
          socialLogin: 'google' | 'apple';
          displayText: boolean;
      }
    | {
          title: string;
          customIcon: 'ai-chat';
      }
);

export type ButtonProps = ButtonOwnProps & React.RefAttributes<any>;

export type VariantColors = {
    defaultBackground: string;
    defaultText: TextColor;
    defaultBorderColor?: string;
    hoverBackground: string;
    hoverText: TextColor;
    hoverBorderColor?: string;
    pressBackground: string;
    pressText: TextColor;
    pressBorderColor?: string;
    inactiveBackground: string;
    inactiveText: TextColor;
    inactiveBorderColor?: string;
};

export type StateStyle = {
    background: string;
    text: TextColor;
    borderColor?: string;
    shadow?: Shadow;
    textStyle?: StyleProp<ViewStyle>;
};

const variantStateStyles: Record<ButtonVariant, StateStyles<StateStyle>> = {
    primary: {
        default: {
            background: colors['Primary-Button-Default'],
            text: 'Dark-Text',
            shadow: shadows.default,
        },
        hovered: {
            background: colors['Primary-Button-Hover'],
            text: 'Dark-Text',
            shadow: shadows.hover,
        },
        pressed: {
            background: colors['Primary-Button-Press'],
            text: 'Dark-Text',
            shadow: shadows.press,
        },
        inactive: {
            background: colors['Primary-Button-Inactive'],
            text: 'Dark-Text',
        },
    },
    secondary: {
        default: {
            background: colors['Secondary-Button-Default'],
            text: 'Dark-Text',
            borderColor: colors['Dark-Text'],
            shadow: shadows.default,
        },
        hovered: {
            background: colors['Secondary-Button-Hover'],
            text: 'Gray-Text',
            borderColor: colors['Secondary-Button-Hover-Border'],
        },
        pressed: {
            background: colors['Secondary-Button-Press'],
            text: 'Dark-Text',
            borderColor: colors['Dark-Text'],
        },
        inactive: {
            background: colors['Secondary-Button-Inactive'],
            text: 'Inactive-Text',
            borderColor: colors['Grey-Light'],
        },
    },
    tertiary: {
        default: {
            background: 'transparent',
            text: 'Dark-Text',
            shadow: shadows.default,
            borderColor: colors['Primary-Button-Default'],
        },
        hovered: {
            background: 'transparent',
            text: 'Dark-Text',
            shadow: shadows.hover,
            borderColor: colors['Primary-Button-Hover'],
        },
        pressed: {
            background: 'transparent',
            text: 'Dark-Text',
            shadow: shadows.press,
            borderColor: colors['Primary-Button-Press'],
        },
        inactive: {
            background: 'transparent',
            text: 'Inactive-Text',
            borderColor: colors['Primary-Button-Inactive'],
        },
    },
    alert: {
        default: {
            background: colors['Alert-Button-Default'],
            text: 'White-Text',
            shadow: shadows.default,
        },
        hovered: {
            background: colors['Alert-Button-Hover'],
            text: 'White-Text',
            shadow: shadows.hover,
        },
        pressed: {
            background: colors['Alert-Button-Press'],
            text: 'White-Text',
            shadow: shadows.press,
        },
        inactive: {
            background: colors['Alert-Button-Inactive'],
            text: 'Inactive-Text',
        },
    },
    text: {
        default: {
            background: 'transparent',
            text: 'Primary-Button-Default',
        },
        hovered: {
            background: 'transparent',
            text: 'Primary-Button-Hover',
        },
        pressed: {
            background: 'transparent',
            text: 'Primary-Button-Press',
        },
        inactive: {
            background: 'transparent',
            text: 'Inactive-Text',
        },
    },
    'text-dark': {
        default: {
            background: 'transparent',
            text: 'Dark-Text',
        },
        hovered: {
            background: 'transparent',
            text: 'Gray-Text',
        },
        pressed: {
            background: 'transparent',
            text: 'Dark-Text',
        },
        inactive: {
            background: 'transparent',
            text: 'Grey-Light',
        },
    },
    ghost: {
        default: {
            background: 'transparent',
            text: 'Gray-Text',
        },
        hovered: {
            background: 'transparent',
            text: 'Dark-Text',
        },
        pressed: {
            background: 'transparent',
            text: 'Gray-Text',
        },
        inactive: {
            background: 'transparent',
            text: 'Grey-Light',
        },
    },
    select: {
        default: {
            background: colors['Secondary-Button-Default'],
            text: 'Dark-Text',
            borderColor: colors['Dark-Text'],
            shadow: shadows.default,
            textStyle: { flex: 1 },
        },
        hovered: {
            background: colors['Secondary-Button-Hover'],
            text: 'Gray-Text',
            borderColor: colors['Secondary-Button-Hover-Border'],
            textStyle: { flex: 1 },
        },
        pressed: {
            background: colors['Secondary-Button-Press'],
            text: 'Dark-Text',
            borderColor: colors['Dark-Text'],
            textStyle: { flex: 1 },
        },
        inactive: {
            background: colors['Secondary-Button-Inactive'],
            text: 'Inactive-Text',
            borderColor: colors['Grey-Light'],
            textStyle: { flex: 1 },
        },
    },
    'list-button': {
        default: {
            background: colors['Secondary-Button-Default'],
            text: 'Dark-Text',
            borderColor: colors['Dark-Text'],
            shadow: shadows.default,
            textStyle: { flex: 1 },
        },
        hovered: {
            background: colors['Secondary-Button-Hover'],
            text: 'Gray-Text',
            borderColor: colors['Secondary-Button-Hover-Border'],
            textStyle: { flex: 1 },
        },
        pressed: {
            background: colors['Secondary-Button-Press'],
            text: 'Dark-Text',
            borderColor: colors['Dark-Text'],
            textStyle: { flex: 1 },
        },
        inactive: {
            background: colors['Secondary-Button-Inactive'],
            text: 'Inactive-Text',
            borderColor: colors['Grey-Light'],
            textStyle: { flex: 1 },
        },
    },
    'pro-header-button': {
        default: {
            background: hexToRgbA(colors['Pro-Drawer-Item-Active'], 0.1),
            text: 'White',
        },
        hovered: {
            background: hexToRgbA(colors['Pro-Drawer-Item-Active'], 0.2),
            text: 'Grey-Light',
            shadow: shadows.hover,
        },
        pressed: {
            background: hexToRgbA(colors['Pro-Drawer-Item-Active'], 0.2),
            text: 'White',
            shadow: shadows.press,
        },
        inactive: {
            background: hexToRgbA(colors['Pro-Drawer-Item-Active'], 0.08),
            text: 'Grey-Light',
        },
    },
};

export const MAX_BUTTON_WIDTH = 335;

const Button: React.FC<ButtonProps> = forwardRef((props, ref) => {
    const isDisabledByParent = useIsDisabledByParent(props.isDisabled);
    const { styles, onPressIn, onPressOut, renderInHoverContainer } = useInteractionStateStyles({
        isDisabled: props.isDisabled,
        stateStyles: variantStateStyles[props.variant || 'primary'],
    });
    const iconSize: IconSize = props.variant === 'ghost' ? 'ghost-button-icon' : 'button-icon';
    const leftIcon = props.leftIcon ? <Icon size={iconSize} name={props.leftIcon} color={styles.text} /> : null;
    const icon = props.icon ? <Icon size={iconSize} name={props.icon} color={props.iconColor || styles.text} /> : null;

    const getContent = () => {
        if ('title' in props) {
            const left = props.leftContent || leftIcon || null;
            const center = (
                <Text
                    variant={props.variant ? 'text-button-text' : 'button'}
                    color={styles.text}
                    style={[props.textStyle, styles.textStyle, { flexShrink: 1, minWidth: 0 }]}
                >
                    {props.title}
                </Text>
            );
            const right = icon ? (
                <Stack
                    horizontal
                    style={{
                        paddingLeft: props.justifyButtonContent === 'flex-start' ? getSpacing(4) : undefined,
                    }}
                >
                    {icon}
                </Stack>
            ) : props.variant === 'select' ? (
                <View />
            ) : null;

            return (
                <Stack
                    flex={1}
                    horizontal
                    align="center"
                    style={{ alignSelf: 'stretch' }}
                    justify={
                        props.variant === 'list-button' || props.variant === 'select'
                            ? 'space-between'
                            : props.justifyButtonContent || 'center'
                    }
                    space={2.5}
                >
                    {props.renderButtonContent ? (
                        props.renderButtonContent({ left, center, right })
                    ) : (
                        <>
                            {left}
                            {center}
                            {right}
                        </>
                    )}
                </Stack>
            );
        } else {
            return (
                <Stack horizontal fullWidth align="center" justify="center" space={5}>
                    {props.socialLogin === 'apple' ? (
                        <AppleLogo height={16.2} width={12.6} />
                    ) : (
                        <GoogleLogo height={15.2} width={15.2} />
                    )}
                    {props.displayText ? (
                        <Text variant="text-button-text" color={styles.text}>
                            {props.socialLogin === 'apple' ? intl('apple') : intl('google')}
                        </Text>
                    ) : null}
                </Stack>
            );
        }
    };

    return (
        <View
            style={[
                props.size === 'lg' ? { width: '100%', maxWidth: props.width || MAX_BUTTON_WIDTH } : {},
                props.style,
            ]}
            ref={ref}
            collapsable={false}
        >
            {renderInHoverContainer(
                <Pressable
                    style={[
                        {
                            width: props.width,
                            justifyContent: 'center',
                            alignItems: 'center',
                            paddingVertical: 8,
                            paddingHorizontal: 10,
                        },
                        {
                            backgroundColor: styles.background,
                            borderColor: styles.borderColor,
                            borderWidth: styles.borderColor ? 1 : 0,
                            borderRadius: 5,
                            height: 42,
                            ...(styles.shadow || {}),
                            ...(props.variant === 'text' && { paddingHorizontal: 0 }),
                            width: props.width,
                        },
                        isDisabledByParent
                            ? {
                                  backgroundColor: styles.background,
                              }
                            : {},
                    ]}
                    onPress={(e) => {
                        if (!isDisabledByParent) {
                            props.onPress(e);
                        }
                    }}
                    onPressIn={onPressIn}
                    onPressOut={onPressOut}
                    testID={props.testID}
                    onLayout={props.onLayout}
                    disabled={isDisabledByParent}
                    collapsable={false}
                >
                    {getContent()}
                </Pressable>,
            )}
        </View>
    );
});

export default React.memo(Button);
